import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type RouterService from '@ember/routing/router-service';
import type StoreService from 'ember-data/store';
import type TourManagerService from './tour-manager';
import type TourStorageService from './tour-storage';
import type ThoughtBubbleStorageService from './thought-bubble-storage';
import type { TourConfig } from './tour-manager';
import type { ThoughtBubbleFlow } from '../types/thought-bubble';
import type ThoughtBubbleFlowInvocationModel from '../models/thought-bubble-flow-invocation';
import { toDriverStep } from '../types/thought-bubble';

/**
 * Runtime service for evaluating and triggering thought bubble flows.
 *
 * On each route transition, evaluates all active flows against:
 * - Current route (exact match or wildcard/glob pattern)
 * - Current domain (window.location.hostname)
 * - User targeting rules (company, employee attributes, first-login)
 * - Completion state (via tourStorage)
 *
 * Note: Role-based targeting is not yet implemented in this MVP.
 * The targeting system currently supports company/employee attribute rules
 * and first-login checks. Role support may be added in a future iteration.
 *
 * Delegates rendering to the existing TourManager + driver.js infrastructure.
 *
 * @memberof services
 */
export default class ThoughtBubbleRuntimeService extends Service {
  @service declare router: RouterService;
  @service declare store: StoreService;
  @service declare tourManager: TourManagerService;
  @service declare tourStorage: TourStorageService;
  @service declare thoughtBubbleStorage: ThoughtBubbleStorageService;

  @tracked isInitialized = false;

  /**
   * Track flows dismissed during this session to avoid pestering users.
   */
  private dismissedThisSession = new Set<string>();

  /**
   * Bound handler for route change events.
   */
  private routeChangeHandler?: () => void;

  /**
   * Pending trigger timeout ID for cancellation on route changes.
   */
  private pendingTriggerTimeout?: ReturnType<typeof setTimeout>;

  /**
   * Pending queue timeout ID for sequential flow chaining.
   */
  private pendingQueueTimeout?: ReturnType<typeof setTimeout>;

  /**
   * Queue of matching flows to invoke sequentially on the current route.
   * When the current flow completes or is dismissed, the next one starts.
   */
  private flowQueue: ThoughtBubbleFlow[] = [];

  /**
   * Active invocation record ID for the currently running flow.
   * Used to PATCH step progress and completion status.
   */
  private activeInvocationId: string | null = null;

  /**
   * The user context object. Set during initialization by the consuming app.
   * Expected shape: { company?: { id: string }, roles: Array<{ relationshipType: string }>, ... }
   */
  private userContext: Record<string, unknown> | null = null;

  /**
   * Initialize the runtime service. Called after user context is available.
   * Subscribes to route transitions and evaluates flows on each change.
   *
   * @param userContext - An object with user/company/role info for targeting evaluation.
   */
  initialize(userContext: Record<string, unknown>) {
    if (this.isInitialized) return;

    this.userContext = userContext;
    this.isInitialized = true;

    this.routeChangeHandler = () => {
      this.evaluateAndTrigger();
    };
    this.router.on('routeDidChange', this.routeChangeHandler);

    // Also evaluate immediately for the current route
    this.evaluateAndTrigger();
  }

  /**
   * Tear down the service: unsubscribe from route events.
   */
  willDestroy() {
    super.willDestroy();
    if (this.routeChangeHandler) {
      this.router.off('routeDidChange', this.routeChangeHandler);
      this.routeChangeHandler = undefined;
    }
    if (this.pendingTriggerTimeout) {
      clearTimeout(this.pendingTriggerTimeout);
      this.pendingTriggerTimeout = undefined;
    }
    if (this.pendingQueueTimeout) {
      clearTimeout(this.pendingQueueTimeout);
      this.pendingQueueTimeout = undefined;
    }
    this.flowQueue = [];
  }

  // ─── Core Evaluation ────────────────────────────────────────────────────

  /**
   * Evaluate all active flows against the current context and trigger
   * matching flows in priority order (0 = highest). When multiple flows
   * match, they are queued and invoked sequentially.
   */
  private async evaluateAndTrigger() {
    try {
      // Don't trigger flows when the app is in visual builder mode
      if (new URLSearchParams(window.location.search).has('tb-builder')) return;

      // Cancel any pending trigger from a previous route change
      if (this.pendingTriggerTimeout) {
        clearTimeout(this.pendingTriggerTimeout);
        this.pendingTriggerTimeout = undefined;
      }
      if (this.pendingQueueTimeout) {
        clearTimeout(this.pendingQueueTimeout);
        this.pendingQueueTimeout = undefined;
      }
      // Clear any queued flows from the previous route
      this.flowQueue = [];

      // Ensure flows are loaded from API (no-op for localStorage mode)
      await this.thoughtBubbleStorage.ensureLoaded();

      const currentRoute = this.router.currentRouteName ?? '';
      const currentUrl = this.router.currentURL ?? window.location.pathname;
      const currentPath = currentUrl.split('?')[0];
      const currentDomain = window.location.hostname;

      const activeFlows = this.thoughtBubbleStorage.getActiveFlows();

      const matchingFlows = activeFlows.filter((flow) => {
        const reasons = {
          route: this.matchesRoute(flow.route, currentRoute, currentPath),
          domain: this.matchesDomain(flow.enabledDomains, currentDomain),
          targeting: this.matchesTargetingRules(flow),
          notCompleted: !this.tourStorage.isTourCompleted(flow.id),
          notDismissed: !this.dismissedThisSession.has(flow.id),
        };
        const passed = Object.values(reasons).every(Boolean);
        if (!passed && this.isDebugEnabled()) {
          console.debug(`[thought-bubble] Flow "${flow.name}" skipped:`, reasons);
        }
        return passed;
      });

      if (matchingFlows.length === 0) return;

      // Sort by priority ascending (0 = highest priority)
      matchingFlows.sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));

      // Queue all matching flows and start the first one
      this.flowQueue = matchingFlows;
      this.triggerNextFlow();
    } catch (e) {
      console.error('Error evaluating thought bubble flows', e);
    }
  }

  /**
   * Trigger the next flow in the queue. Called after the current flow
   * completes or is dismissed.
   */
  private triggerNextFlow() {
    const flow = this.flowQueue.shift();
    if (!flow) return;
    this.triggerFlow(flow);
  }

  /**
   * Convert a ThoughtBubbleFlow to a TourConfig and start it via tourManager.
   */
  private triggerFlow(flow: ThoughtBubbleFlow) {
    // Reset invocation tracking for this flow
    this.activeInvocationId = null;
    let dismissedByClose = false;

    const sortedSteps = flow.steps.sort((a, b) => a.order - b.order);
    const totalSteps = sortedSteps.length;

    const tourConfig: TourConfig = {
      id: flow.id,
      name: flow.name,
      steps: sortedSteps.map(toDriverStep),
      driverOptions: {
        showProgress: totalSteps > 1,
        showButtons: totalSteps > 1 ? ['next', 'previous', 'close'] : ['next', 'close'],
        doneBtnText: totalSteps > 1 ? 'Done' : 'Got it',
        onCloseClick: () => {
          dismissedByClose = true;
        },
        onHighlighted: (_element, _step, { state }) => {
          const stepIndex = state.activeIndex ?? 0;
          this.trackStepProgress(flow.id, stepIndex, totalSteps);
        },
        onDestroyed: () => {
          if (!dismissedByClose) {
            this.trackFlowCompletion();
          }
          this.activeInvocationId = null;
          this.dismissedThisSession.add(flow.id);
          // After this flow completes or is dismissed, trigger the next queued flow
          if (this.flowQueue.length > 0) {
            this.pendingQueueTimeout = setTimeout(() => {
              this.pendingQueueTimeout = undefined;
              this.triggerNextFlow();
            }, 300);
          }
        },
      },
    };

    this.tourManager.registerTour(tourConfig);

    // Delay slightly to let the DOM settle after route transition
    this.pendingTriggerTimeout = setTimeout(() => {
      this.pendingTriggerTimeout = undefined;
      try {
        this.tourManager.startTour(flow.id);
      } catch (e) {
        console.error('Error starting thought bubble tour', e);
      }
    }, 200);
  }

  // ─── Invocation Tracking ───────────────────────────────────────────────

  /**
   * Track step progress for a flow invocation.
   * On the first step (index 0), creates the invocation record if none is active.
   * On subsequent steps (or revisiting step 0), updates the steps_completed field.
   * All API calls are fire-and-forget.
   */
  private trackStepProgress(flowId: string, stepIndex: number, totalSteps: number) {
    if (stepIndex === 0 && !this.activeInvocationId) {
      this.createInvocationRecord(flowId, totalSteps);
    } else {
      this.updateStepProgress(stepIndex, totalSteps);
    }
  }

  /**
   * Create a new invocation record when a flow starts (step 0).
   * Sets activeInvocationId immediately so step updates can be tracked
   * even while the initial POST is in-flight.
   */
  private async createInvocationRecord(flowId: string, totalSteps: number) {
    const invocationNonce = `${flowId}-${Date.now()}`;
    // Set immediately so subsequent onHighlighted calls can update progress
    // before the save resolves, and so we don't create duplicates on step 0.
    this.activeInvocationId = invocationNonce;

    try {
      const record = this.store.createRecord('thought-bubble-flow-invocation', {
        flowId,
        stepsCompleted: { 0: true, total: totalSteps },
        completed: false,
        browserMeta: this.getBrowserMeta(),
      }) as ThoughtBubbleFlowInvocationModel;
      await record.save();
      // Only update to the server-assigned ID if this is still the active invocation.
      // Prevents a stale in-flight save from overwriting a newer invocation.
      if (this.activeInvocationId === invocationNonce) {
        this.activeInvocationId = record.id;
      }
    } catch (e) {
      // If save failed and this is still the active nonce, clear it so
      // subsequent step updates don't try to PATCH a non-existent record.
      if (this.activeInvocationId === invocationNonce) {
        this.activeInvocationId = null;
      }
      console.error('[thought-bubble] Failed to create invocation record', e);
    }
  }

  /**
   * Update step progress on the active invocation record.
   */
  private async updateStepProgress(stepIndex: number, totalSteps: number) {
    if (!this.activeInvocationId) return;
    try {
      const record = this.store.peekRecord(
        'thought-bubble-flow-invocation',
        this.activeInvocationId
      ) as ThoughtBubbleFlowInvocationModel | null;
      if (!record) return;
      record.stepsCompleted = {
        ...record.stepsCompleted,
        [stepIndex]: true,
        total: totalSteps,
      };
      await record.save();
    } catch (e) {
      console.error('[thought-bubble] Failed to update step progress', e);
    }
  }

  /**
   * Mark the active invocation as completed.
   */
  private async trackFlowCompletion() {
    if (!this.activeInvocationId) return;
    try {
      const record = this.store.peekRecord(
        'thought-bubble-flow-invocation',
        this.activeInvocationId
      ) as ThoughtBubbleFlowInvocationModel | null;
      if (!record) return;
      record.completed = true;
      await record.save();
    } catch (e) {
      console.error('[thought-bubble] Failed to mark invocation completed', e);
    }
  }

  /**
   * Collect browser metadata for the invocation record.
   */
  private getBrowserMeta(): Record<string, string> {
    const ua = navigator.userAgent;
    return {
      device_type: /Mobi|Android/i.test(ua) ? 'mobile' : 'desktop',
      os: this.detectOS(ua),
      browser_name: this.detectBrowser(ua),
      browser_version: this.detectBrowserVersion(ua),
    };
  }

  private detectOS(ua: string): string {
    if (ua.includes('Windows')) return 'Windows';
    if (ua.includes('Android')) return 'Android';
    if (/iPhone|iPad|iPod/.test(ua)) return 'iOS';
    if (ua.includes('Mac OS')) return 'macOS';
    if (ua.includes('Linux')) return 'Linux';
    return 'Unknown';
  }

  private detectBrowser(ua: string): string {
    if (ua.includes('Edg/')) return 'Edge';
    if (ua.includes('Chrome') && !ua.includes('Edg/')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
    return 'Unknown';
  }

  private detectBrowserVersion(ua: string): string {
    const patterns: [string, RegExp][] = [
      ['Edge', /Edg\/(\d+[\d.]*)/],
      ['Chrome', /Chrome\/(\d+[\d.]*)/],
      ['Firefox', /Firefox\/(\d+[\d.]*)/],
      ['Safari', /Version\/(\d+[\d.]*)/],
    ];
    for (const [, regex] of patterns) {
      const match = ua.match(regex);
      if (match?.[1]) return match[1];
    }
    return 'Unknown';
  }

  // ─── Route Matching ─────────────────────────────────────────────────────

  /**
   * Check if a flow's route pattern matches the current route or URL path.
   *
   * Accepts BOTH formats:
   * - URL paths: '/people', '/people/*', '/companies/123/**'
   * - Ember route names: 'authenticated.people.index', 'authenticated.companies.show.*'
   *
   * URL paths (starting with '/') are matched against the current URL pathname.
   * Ember route names (dot-separated) are matched against router.currentRouteName.
   *
   * Supports wildcards in both modes:
   * - '*' matches one segment
   * - '**' matches any depth of segments
   */
  matchesRoute(pattern: string, currentRoute: string, currentPath?: string) {
    if (!pattern) return false;

    const isUrlPattern = pattern.startsWith('/');

    if (isUrlPattern) {
      // Match against URL path
      const target = currentPath ?? window.location.pathname;
      return this.matchPattern(pattern, target, '/');
    }

    // Match against Ember route name
    return this.matchPattern(pattern, currentRoute, '.');
  }

  /**
   * Generic pattern matching with a configurable separator.
   * Supports exact match, single wildcard (*), and globstar (**).
   */
  private matchPattern(pattern: string, value: string, separator: string) {
    // Normalize: strip trailing separator
    const normPattern = pattern.endsWith(separator) ? pattern.slice(0, -separator.length) : pattern;
    const normValue = value.endsWith(separator) ? value.slice(0, -separator.length) : value;

    // Exact match
    if (normPattern === normValue) return true;

    // Build regex: escape the separator, then handle wildcards
    const escapedSep = separator === '.' ? '\\.' : '\\/';
    const segmentChar = separator === '.' ? '[^.]+' : '[^/]+';

    const regexStr = normPattern
      .replace(separator === '.' ? /\./g : /\//g, escapedSep)
      .replace(/\*\*/g, '__GLOBSTAR__')
      .replace(/\*/g, segmentChar)
      .replace(new RegExp(`${escapedSep}__GLOBSTAR__`, 'g'), `(?:${escapedSep}.*)?`)
      .replace(/__GLOBSTAR__/g, '.*');

    try {
      const regex = new RegExp(`^${regexStr}$`);
      return regex.test(normValue);
    } catch {
      return false;
    }
  }

  // ─── Domain Matching ────────────────────────────────────────────────────

  /**
   * Check if the current domain is in the flow's enabled domains list.
   * Empty list = all domains allowed.
   *
   * Matches by base domain (everything after the first subdomain) so that
   * client-specific employee subdomains like "acme.tio-vdk.io" match a
   * configured domain like "employee.tio-vdk.io" (both share "tio-vdk.io").
   */
  matchesDomain(enabledDomains: string[], currentDomain: string) {
    if (enabledDomains.length === 0) return true;
    // Always match on localhost (local development)
    if (currentDomain === 'localhost' || currentDomain === '127.0.0.1' || currentDomain === '::1')
      return true;

    const baseDomain = (host: string) => {
      const idx = host.indexOf('.');
      return idx === -1 ? host : host.slice(idx + 1);
    };

    const currentBase = baseDomain(currentDomain);

    return enabledDomains.some(
      (domain) => currentDomain === domain || baseDomain(domain) === currentBase
    );
  }

  // ─── Targeting Rules ────────────────────────────────────────────────────

  /**
   * Evaluate targeting rules against the current user context.
   */
  matchesTargetingRules(flow: ThoughtBubbleFlow) {
    if (!this.userContext) return true;

    const { rules, matchMode, firstLoginOnly } = flow.targetingRules;
    const checks: boolean[] = [];

    // Evaluate each attribute rule
    for (const rule of rules) {
      const actual = this.getAttributeValue(rule.source, rule.attribute);
      const match = actual !== undefined && String(actual) === rule.value;
      checks.push(match);
    }

    // First login check
    if (firstLoginOnly) {
      checks.push(this.isFirstLogin());
    }

    // If no rules configured, allow all
    if (checks.length === 0) return true;

    const mode = matchMode || 'all';
    return mode === 'all' ? checks.every(Boolean) : checks.some(Boolean);
  }

  /**
   * Resolve an attribute value from the user context by source + attribute name.
   */
  private getAttributeValue(source: 'company' | 'employee', attribute: string): unknown {
    if (source === 'company') {
      const company = this.userContext?.['company'] as Record<string, unknown> | undefined;
      return company?.[attribute];
    }
    const employee = this.userContext?.['employee'] as Record<string, unknown> | undefined;
    return employee?.[attribute];
  }

  // ─── User Context Helpers ───────────────────────────────────────────────

  private isFirstLogin(): boolean {
    const logins = this.userContext?.['logins'] as Array<unknown> | undefined;
    // If login history is not available, we cannot safely determine first login
    if (!Array.isArray(logins)) {
      return false;
    }
    return logins.length <= 1;
  }

  /**
   * Check if debug logging is enabled via sessionStorage flag.
   * Set `sessionStorage.setItem('tb-debug', '1')` to enable.
   */
  private isDebugEnabled(): boolean {
    try {
      return sessionStorage.getItem('tb-debug') === '1';
    } catch {
      return false;
    }
  }

  // ─── Public API ─────────────────────────────────────────────────────────

  /**
   * Manually dismiss a flow for this session.
   */
  dismissFlow(flowId: string) {
    this.dismissedThisSession.add(flowId);
  }

  /**
   * Reset all session dismissals (useful for testing).
   */
  resetDismissals() {
    this.dismissedThisSession.clear();
  }

  /**
   * Force-show a specific flow regardless of targeting/completion state.
   * Useful for preview/testing in the admin UI.
   */
  forceShowFlow(flowId: string) {
    const flow = this.thoughtBubbleStorage.getFlow(flowId);
    if (!flow) {
      console.warn(`Thought bubble flow not found: ${flowId}`);
      return;
    }
    this.triggerFlow(flow);
  }
}

declare module '@ember/service' {
  interface Registry {
    'thought-bubble-runtime': ThoughtBubbleRuntimeService;
  }
}
